import React, { useCallback, useState } from 'react';
import type { FC } from 'react';
import { useIntl, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Form, { Field, FormFooter, CharacterCounterField } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { CloseButton, ModalBody, ModalFooter, useModal } from '@atlaskit/modal-dialog';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import TextArea from '@atlaskit/textarea';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';

import { messages } from '../messages';

type CreateSnippetModalHeaderProps = {
	onClose: () => void;
};

type CreateSnippetModalDialogProps = {
	onCloseComplete: () => void;
} & CreateSnippetModalHeaderProps;

type CreateSnippetModalProps = {
	adf: any;
} & CreateSnippetModalDialogProps;

export enum CreateSnippetModalEvents {
	CANCELLED = 'createSnippetModalCancelled',
	CLOSED = 'createSnippetModalClosed',
	CREATED = 'createSnippetModalCreated',
}

export const MAX_SNIPPET_NAME_LENGTH = 60;
export const MAX_SNIPPET_DESCRIPTION_LENGTH = 120;

const styles = cssMap({
	headerContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		flexDirection: 'row',
		padding: token('space.300'),
	},
	headerText: {
		display: 'flex',
		alignItems: 'flex-start',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	bodyContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		height: '100%',
	},
	contentContainer: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		maxHeight: '340px',
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.large'),
		scrollbarColor: `${token('color.border.input')} transparent`,
	},
	adfContainer: {
		overflow: 'auto',
		height: '100%',
		padding: token('space.300'),
	},
});

const CreateSnippetModalHeader = ({ onClose }: CreateSnippetModalHeaderProps) => {
	const { titleId } = useModal();

	return (
		<Box xcss={styles.headerContainer}>
			<Box xcss={styles.headerText}>
				<Heading size="medium" id={titleId}>
					<FormattedMessage {...messages.createTemplateModalTitle} />
				</Heading>
				<Text size="medium" color="color.text.subtle">
					<FormattedMessage {...messages.createTemplateModalDescription} />
				</Text>
			</Box>
			<CloseButton onClick={onClose} />
		</Box>
	);
};

export const CreateSnippetModal: FC<CreateSnippetModalProps> = ({ adf, onClose }) => {
	const [adfError, setADFError] = useState<any>(null);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();
	const intl = useIntl();

	const fireCreateSnippetModalAnalytics = useCallback(
		(event: CreateSnippetModalEvents) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceCreateSnippetModal',
					action: 'closed',
					actionSubject: 'button',
					actionSubjectId: 'createSnippet',
					type: 'ui',
					attributes: {
						event,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const exitModal = useCallback(
		(event: CreateSnippetModalEvents) => {
			fireCreateSnippetModalAnalytics(event);
			onClose();
		},
		[fireCreateSnippetModalAnalytics, onClose],
	);

	const createSnippet = useCallback(() => {
		fireCreateSnippetModalAnalytics(CreateSnippetModalEvents.CREATED);
		onClose();
	}, [fireCreateSnippetModalAnalytics, onClose]);

	const handleADFError = useCallback(
		(error: any) => {
			setADFError(error);
		},
		[setADFError],
	);

	return (
		<Modal height="750px">
			<Form onSubmit={() => createSnippet()}>
				{({ getState, formProps }) => (
					<form {...formProps}>
						<CreateSnippetModalHeader onClose={() => exitModal(CreateSnippetModalEvents.CLOSED)} />

						<ModalBody>
							<Box xcss={styles.bodyContainer}>
								<Box>
									<Field
										name="snippet-name"
										label={intl.formatMessage(messages.createTemplateModalNameLabel)}
										defaultValue=""
										helperMessage={intl.formatMessage(messages.createTemplateModalNameHelper)}
										component={({ fieldProps }) => (
											<Textfield
												{...fieldProps}
												maxLength={MAX_SNIPPET_NAME_LENGTH}
												placeholder={intl.formatMessage(
													messages.createTemplateModalNamePlaceholder,
												)}
											/>
										)}
										validate={(value) =>
											!!value
												? undefined
												: intl.formatMessage(messages.createTemplateModalNameError)
										}
										isRequired
									/>
									<CharacterCounterField<string, HTMLTextAreaElement>
										name="snippet-description"
										label={intl.formatMessage(messages.createTemplateModalDescriptionLabel)}
										maxCharacters={MAX_SNIPPET_DESCRIPTION_LENGTH}
									>
										{({ fieldProps }) => (
											<TextArea
												{...fieldProps}
												placeholder={intl.formatMessage(
													messages.createTemplateModalDescriptionPlaceholder,
												)}
											/>
										)}
									</CharacterCounterField>
								</Box>
								<Box xcss={styles.contentContainer}>
									<Box xcss={styles.adfContainer}>
										<ADFRenderer
											document={adf}
											appearance="comment"
											allowPlaceholderText
											onError={handleADFError}
										/>
									</Box>
								</Box>
							</Box>
						</ModalBody>

						<ModalFooter>
							<FormFooter>
								<Button
									appearance="subtle"
									onClick={() => exitModal(CreateSnippetModalEvents.CANCELLED)}
								>
									<FormattedMessage {...messages.createTemplateModalCancelButton} />
								</Button>
								<Button
									type="submit"
									appearance="primary"
									isDisabled={!getState().dirty || getState().hasValidationErrors || !!adfError}
								>
									<FormattedMessage {...messages.createTemplateModalCreateButton} />
								</Button>
							</FormFooter>
						</ModalFooter>
					</form>
				)}
			</Form>
		</Modal>
	);
};
